package com.tovtalk.ui.screen.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.onboarding.OnboardingStep
import com.tovtalk.presentation.viewmodel.onboarding.OnboardingViewModel
import com.tovtalk.ui.components.TovTalkFullScreenLoading
import com.tovtalk.ui.platform.PlatformBackHandler
import com.tovtalk.utils.orientation.LockPortraitOrientation
import com.tovtalk.utils.strings.StringProvider

/**
 * Root container for the 5-step guest onboarding flow.
 * Drives the [OnboardingViewModel] state machine and renders the appropriate screen.
 *
 * Navigation to Home is gated on [OnboardingViewModel.commitState] being
 * [UiState.Success] — prevents the guest from landing on an empty feed
 * before the guest token and preferences are persisted.
 */
@Composable
fun OnboardingFlow(
    viewModel: OnboardingViewModel,
    onDone: () -> Unit,
) {
    LockPortraitOrientation()

    val state by viewModel.state.collectAsState()
    val commitState by viewModel.commitState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.start()
    }

    // Fire onDone only once commit has succeeded (and we're in Done step).
    LaunchedEffect(state.step, commitState) {
        if (state.step == OnboardingStep.Done && commitState is UiState.Success) {
            onDone()
        }
    }

    // Intercept the platform back gesture on every step past Welcome so it walks
    // the user back through the flow rather than exiting the app.
    PlatformBackHandler(
        enabled = state.step != OnboardingStep.Welcome && state.step != OnboardingStep.Done,
        onBack = { viewModel.goBack() },
    )

    val goBack: () -> Unit = { viewModel.goBack() }

    Box(modifier = Modifier.fillMaxSize()) {
        when (state.step) {
            OnboardingStep.Welcome -> WelcomeScreen(onContinue = viewModel::advance)
            OnboardingStep.Notifications -> NotificationsPermissionScreen(
                onRequestPermission = { viewModel.requestNotifications() },
                onAdvance = viewModel::advance,
                onBack = goBack,
            )
            OnboardingStep.Kashrut -> KashrutLevelScreen(
                levels = state.kashrutLevels,
                selectedId = state.selectedKashrutLevelId,
                onSelect = viewModel::selectKashrut,
                onAdvance = viewModel::advance,
                onBack = goBack,
            )
            OnboardingStep.Categories -> CategoriesScreen(
                categories = state.categories,
                selectedIds = state.selectedCategoryIds,
                onToggle = viewModel::toggleCategory,
                onAdvance = viewModel::advance,
                onBack = goBack,
            )
            OnboardingStep.Done -> Unit
        }

        // Blocking loader overlay while commit is in flight.
        // `clickable` with null indication absorbs taps so the user can't
        // hit buttons on the step underneath while the commit is running.
        if (commitState is UiState.Loading) {
            val overlayInteractionSource = remember { MutableInteractionSource() }
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.35f))
                    .clickable(
                        interactionSource = overlayInteractionSource,
                        indication = null,
                        onClick = {},
                    ),
                contentAlignment = Alignment.Center,
            ) {
                TovTalkFullScreenLoading(
                    message = StringProvider.get("onboarding_committing_message"),
                )
            }
        }

        // Error dialog shown after all automatic retries are exhausted.
        if (commitState is UiState.Error) {
            OnboardingCommitErrorDialog(
                errorMessage = (commitState as UiState.Error).message,
                onRetry = viewModel::retryCommit,
                onCancel = viewModel::cancelCommit,
            )
        }
    }
}
